"use client";

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { cn } from "@punchup/utils/cn";

export interface UnderlineTab {
  value: string;
  label: React.ReactNode;
}

interface UnderlineTabsProps {
  tabs: UnderlineTab[];
  activeTab: string;
  onTabChange: (value: string) => void;
  /** Right-aligned action buttons rendered inside the nav, sharing the
   *  underline border (e.g. the newsletter overview's View calendar + Create
   *  CTAs). */
  actions?: React.ReactNode;
  className?: string;
}

const ACTIVE_COLOR = "rgb(41, 51, 60)";
const INACTIVE_COLOR = "rgb(120, 123, 126)";
const BORDER_COLOR = "rgb(199, 200, 201)";

interface NavCSSProperties extends React.CSSProperties {
  "--nav-color": string;
  "--nav-active-color": string;
  "--nav-border-color": string;
}

const SLIDER_TRANSITION = "transform 300ms ease, width 300ms ease";

export function UnderlineTabs({
  tabs,
  activeTab,
  onTabChange,
  actions,
  className,
}: UnderlineTabsProps) {
  const navRef = useRef<HTMLElement>(null);
  const tabRefs = useRef<Map<string, HTMLElement>>(new Map());
  const [sliderStyle, setSliderStyle] = useState<{
    left: number;
    top: number;
    width: number;
  } | null>(null);
  // Row height drives a tiled background gradient that draws the full-width
  // underline across the whole (scrollable) tab strip, not just the visible box.
  const [rowHeight, setRowHeight] = useState<number | null>(null);
  const hasAnimated = useRef(false);

  // Reads refs directly; depends only on the active value primitive.
  const measureActiveTab = useCallback(() => {
    const nav = navRef.current;
    if (!nav) return;

    const firstTab = tabRefs.current.values().next().value;
    if (firstTab) {
      const h = firstTab.getBoundingClientRect().height;
      setRowHeight((prev) => (prev === h ? prev : h));
    }

    const tab = tabRefs.current.get(activeTab);
    if (!tab) {
      setSliderStyle(null);
      return;
    }
    const navRect = nav.getBoundingClientRect();
    const tabRect = tab.getBoundingClientRect();
    setSliderStyle({
      left: tabRect.left - navRect.left + nav.scrollLeft,
      // Sit the 2px slider directly on the strip's bottom border.
      top: tabRect.bottom - navRect.top + nav.scrollTop - 2,
      width: tabRect.width,
    });
  }, [activeTab]);

  // Re-measure when string labels (and therefore tab widths) change, e.g. a
  // label going from "Tagged Shows (2)" to "Tagged Shows (3)".
  const labelsKey = useMemo(
    () =>
      tabs
        .map(
          (tab) =>
            `${tab.value}:${typeof tab.label === "string" ? tab.label : ""}`
        )
        .join("|"),
    [tabs]
  );

  // Measure before paint, then scroll the active tab into view when overflowing.
  useLayoutEffect(() => {
    measureActiveTab();
    const nav = navRef.current;
    const el = tabRefs.current.get(activeTab);
    if (nav && el && nav.scrollWidth > nav.clientWidth) {
      el.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "nearest",
      });
    }
  }, [measureActiveTab, labelsKey, activeTab]);

  // Enable the slide transition only after the first measurement.
  useEffect(() => {
    if (sliderStyle) hasAnimated.current = true;
  }, [sliderStyle]);

  // Re-measure on container resize / font loading.
  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;
    const ro = new ResizeObserver(() => measureActiveTab());
    ro.observe(nav);
    return () => ro.disconnect();
  }, [measureActiveTab]);

  const navStyle: NavCSSProperties = {
    color: INACTIVE_COLOR,
    "--nav-color": INACTIVE_COLOR,
    "--nav-active-color": ACTIVE_COLOR,
    "--nav-border-color": BORDER_COLOR,
    // Tiled gradient draws a 1px line at the bottom of the row spanning the
    // full content width. Falls back to a single bottom line before row height
    // is measured (e.g. SSR / first paint).
    backgroundImage: `linear-gradient(to bottom, transparent calc(100% - 1px), var(--nav-border-color) calc(100% - 1px))`,
    backgroundSize: rowHeight ? `100% ${rowHeight}px` : "100% 100%",
    backgroundRepeat: "repeat-y",
  };

  return (
    <nav
      ref={navRef}
      role="tablist"
      className={cn(
        "relative flex flex-row flex-nowrap overflow-x-auto hide-scrollbar text-[16px] font-medium",
        className
      )}
      style={navStyle}
    >
      {tabs.map((tab) => {
        const isActive = activeTab === tab.value;
        return (
          <button
            key={tab.value}
            ref={(el) => {
              if (el) tabRefs.current.set(tab.value, el);
              else tabRefs.current.delete(tab.value);
            }}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onTabChange(tab.value)}
            className={cn(
              "relative flex flex-col justify-end items-center px-5 bg-transparent leading-tight outline-none",
              "text-[var(--nav-color)] transition-colors duration-200 ease-in-out",
              "hover:text-[var(--nav-active-color)] focus-visible:text-[var(--nav-active-color)] focus-visible:font-semibold",
              isActive && "font-semibold text-[var(--nav-active-color)]"
            )}
          >
            <span className="pb-2 pt-3 whitespace-nowrap">{tab.label}</span>
          </button>
        );
      })}
      {actions && (
        // py-2 is load-bearing geometry, not decoration: it grows the actions box,
        // which sets the nav's measured rowHeight and therefore where the underline
        // background-gradient paints. Removing it re-collides the CTAs with the line.
        <div className="ml-auto flex shrink-0 items-center gap-2 self-center py-2 pl-4">
          {actions}
        </div>
      )}
      {sliderStyle && (
        <div
          aria-hidden
          className="absolute left-0 top-0 z-[1] h-[2px]"
          style={{
            backgroundColor: ACTIVE_COLOR,
            width: sliderStyle.width,
            transform: `translate(${sliderStyle.left}px, ${sliderStyle.top}px)`,
            transition: hasAnimated.current ? SLIDER_TRANSITION : "none",
          }}
        />
      )}
    </nav>
  );
}
